// Shared contracts for team members, rates and workspace settings. Owned by the team feature.
import { z } from 'zod';
import { ROLES } from './roles';

/**
 * GET /api/users returns TeamMember[] (everyone in the tenant; members get a reduced list
 * of active users for pickers). Query: ?active=true|false|all (default true) &search=text
 */
export const teamMemberSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string(),
  role: z.enum(ROLES),
  active: z.boolean(),
  avatarUrl: z.string().nullable(),
  timezone: z.string().nullable(),
  /** Current user-level rates (admins only; null otherwise). */
  billRateCents: z.number().int().nullable(),
  costRateCents: z.number().int().nullable(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const RATE_SCOPES = ['workspace', 'user', 'project', 'project_user'] as const;
export type RateScope = (typeof RATE_SCOPES)[number];

export const rateSchema = z.object({
  id: z.uuid(),
  scope: z.enum(RATE_SCOPES),
  userId: z.uuid().nullable(),
  projectId: z.uuid().nullable(),
  billRateCents: z.number().int().nullable(),
  costRateCents: z.number().int().nullable(),
  /** YYYY-MM-DD; the rate applies to entries starting on/after this date. */
  effectiveFrom: z.string(),
});
export type Rate = z.infer<typeof rateSchema>;

const cents = z.number().int().min(0).nullish();

export const rateInputSchema = z
  .object({
    scope: z.enum(RATE_SCOPES),
    userId: z.uuid().nullish(),
    projectId: z.uuid().nullish(),
    billRateCents: cents,
    costRateCents: cents,
    effectiveFrom: z.iso.date().optional(),
    /**
     * Extension: when true, the given field(s) replace the rate history at this scope target —
     * later rates at the same level stop overriding them (Clockify's "change for all past entries").
     * A field sent as null with replaceHistory is removed at this level for all dates (entries then
     * fall back to the next level); existing entry snapshots are not recalculated. A request that only
     * clears fields answers 204 No Content.
     */
    replaceHistory: z.boolean().optional(),
  })
  .refine(
    (r) =>
      (r.scope === 'workspace' && !r.userId && !r.projectId) ||
      (r.scope === 'user' && !!r.userId && !r.projectId) ||
      (r.scope === 'project' && !r.userId && !!r.projectId) ||
      (r.scope === 'project_user' && !!r.userId && !!r.projectId),
    { message: 'userId/projectId do not match the rate scope' },
  )
  .refine(
    (r) => {
      // A null only means something when clearing a field across the history (replaceHistory).
      const given = (v: number | null | undefined) => (r.replaceHistory ? v !== undefined : v != null);
      return given(r.billRateCents) || given(r.costRateCents);
    },
    { message: 'Set a billable or cost rate', path: ['billRateCents'] },
  );
export type RateInput = z.input<typeof rateInputSchema>;

/** Resolved hourly rates for one entry (minor units of the tenant currency). */
export type ResolvedRates = { billRateCents: number | null; costRateCents: number | null };

/** Workspace UI themes (tweakcn palettes, all with the Taim primary #213c99, except Clockify's own blue). */
export const THEMES = ['claymorphism', 'clean-slate', 'clockify', 'nature'] as const;
export type Theme = (typeof THEMES)[number];
export const DEFAULT_THEME: Theme = 'claymorphism';
export const THEME_LABELS: Record<Theme, string> = {
  claymorphism: 'Claymorphism',
  'clean-slate': 'Clean Slate',
  clockify: 'Clockify',
  nature: 'Nature',
};

export const tenantSettingsSchema = z.object({
  name: z.string(),
  timezone: z.string(),
  weekStart: z.number().int().min(0).max(6),
  currency: z.string().length(3),
  theme: z.enum(THEMES),
  /** New and edited time entries need a project (Clockify's required project). */
  requireProject: z.boolean(),
});
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

export const tenantSettingsInputSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    timezone: z.string().min(1),
    weekStart: z.number().int().min(0).max(6),
    currency: z
      .string()
      .transform((s) => s.trim().toUpperCase())
      .pipe(z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO code')),
    theme: z.enum(THEMES),
    requireProject: z.boolean(),
  })
  .partial();
export type TenantSettingsInput = z.input<typeof tenantSettingsInputSchema>;

/** PATCH /api/users/:id (admin). */
export const updateMemberInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    role: z.enum(ROLES),
    active: z.boolean(),
    /** IANA zone; null = use the workspace timezone. */
    timezone: z.string().min(1).nullable(),
  })
  .partial()
  .refine((u) => Object.values(u).some((v) => v !== undefined), { message: 'Nothing to update' });
export type UpdateMemberInput = z.input<typeof updateMemberInputSchema>;

/** POST /api/users (admin): pre-invite someone from the workspace's Google domain. */
export const inviteMemberInputSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email({ message: 'Enter a valid email address' })),
  name: z.string().trim().min(1).max(200).optional(),
  role: z.enum(ROLES).default('member'),
});
export type InviteMemberInput = z.input<typeof inviteMemberInputSchema>;

/** POST /api/rates/recalculate (admin): re-apply current rates to entries starting in [from, to]. */
export const recalculateInputSchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    userIds: z.array(z.uuid()).optional(),
    projectIds: z.array(z.uuid()).optional(),
    /**
     * Also re-rate entries imported from Clockify. Off by default: imported entries carry the
     * rate Clockify billed them at, which Taim's current rate history does not know.
     */
    includeImported: z.boolean().default(false),
  })
  .refine((r) => r.from <= r.to, { message: '"from" must not be after "to"', path: ['to'] });
export type RecalculateInput = z.input<typeof recalculateInputSchema>;
/** `updated` = number of entries whose bill/cost snapshot changed. */
export type RecalculateResult = { updated: number };

/*
 * Rebuild rates from imported history (admin).
 *
 * GET  /api/rates/rebuild-from-import → RateRebuildProposal (read-only)
 * POST /api/rates/rebuild-from-import  (RateRebuildApplyInput) → RateRebuildResult
 * POST /api/rates/rebuild-from-import/preview  (RateRebuildApplyInput) → RateRebuildPreview (writes nothing)
 *
 * Imported Clockify entries carry the rate they were billed at, but Clockify's rate settings are
 * not in the export. The proposal reconstructs user, project and project_user rates (with
 * effective dates) that reproduce those snapshots; imported entries themselves are never re-rated.
 * The rates also decide the rate of all time tracked from now on, so the proposal says which
 * people and projects would bill differently from tomorrow than their history suggests, and which
 * per-project rates would not follow later changes of a person's default rate.
 */

/** The scopes a rebuild proposes (workspace rates are never inferred). */
export const REBUILD_RATE_SCOPES = ['user', 'project', 'project_user'] as const;
export type RebuildRateScope = (typeof REBUILD_RATE_SCOPES)[number];

export type ProposedRate = {
  scope: RebuildRateScope;
  userId: string | null;
  projectId: string | null;
  /**
   * null = this row does not set the field (resolution falls through, as for any rate). A
   * `project_user` row with both fields null ends the person's per-project and project-wide rates
   * on the project from its date: they follow the person's default there.
   */
  billRateCents: number | null;
  costRateCents: number | null;
  /** YYYY-MM-DD in the workspace timezone. */
  effectiveFrom: string;
};

/**
 * Why an imported entry is not reproduced: `no_project` (entries without a project can only use
 * the person's default rate), `same_day_rates` (one person, project and day had several rates;
 * rates change per day at most) or `rates` (the rate rows resolve differently).
 */
export type RebuildMismatchReason = 'no_project' | 'same_day_rates' | 'rates';

export type RebuildMismatch = {
  userId: string;
  projectId: string | null;
  date: string;
  field: 'bill' | 'cost';
  expectedCents: number;
  gotCents: number | null;
  /** Imported entries with this user, project, day and rate. */
  entries: number;
  reason: RebuildMismatchReason;
};

export type RebuildReplay = {
  /** Finished imported entries. */
  entries: number;
  /** Entries whose imported bill and cost rates (where set) resolve exactly. */
  matches: number;
  mismatches: number;
  mismatchesByReason: Record<RebuildMismatchReason, number>;
  /** The largest mismatches (at most 50). */
  samples: RebuildMismatch[];
};

export type RebuildConflict = {
  proposed: ProposedRate;
  /**
   * An existing rate for the same scope, person and project that the proposal does not hold: the
   * one at the same date if there is one, else the one in effect on the proposed date (or the
   * earliest). Existing rates are never changed silently.
   */
  existing: Rate;
};

/**
 * A rate from tomorrow that differs from the confirmed current rates (see CurrentRates), for bill
 * or cost rates (`field`):
 *  - `person_default`: the rate a new project (or time without a project) would get, against the
 *    person's confirmed current default;
 *  - `pair`: one person on one of their recent projects (or a project the admin added), against
 *    its confirmed current rate (its own, or the default it follows).
 * With the accepted or edited proposal alone this is empty by construction; existing rates that
 * are kept can still make it differ.
 */
export type FutureRateChange = {
  kind: 'person_default' | 'pair';
  field: 'bill' | 'cost';
  userId: string;
  /** Always null for `person_default`. */
  projectId: string | null;
  /** The pair's last imported rate (its rate with the most time that day), or the proposed default. */
  lastImportedRate: number;
  /** The pair's last imported day, or the person's. */
  lastDate: string;
  /** The confirmed current rate. */
  expectedRate: number;
  /** The rate the rates resolve for tomorrow (null = no rate). */
  rateFromTomorrow: number | null;
  /** Kept for older clients: always false (only recent projects are checked). */
  stale: boolean;
};

/**
 * A proposed per-project (project_user) or project-wide (project) rate run that equals the
 * person's default: over its whole span (it only ends an earlier per-project rate), or at the end
 * of history (it stays when the default changes later, e.g. a raise set in Taim). Reported because
 * the days did not allow the default itself to carry the earlier rate. For a project run, the
 * default of everyone who worked on it.
 */
export type PinnedRate = {
  scope: 'project' | 'project_user';
  userId: string | null;
  projectId: string;
  field: 'bill' | 'cost';
  rateCents: number;
  effectiveFrom: string;
  /** The next rate of the same person and project (or project), or null: in force from now on. */
  until: string | null;
};

/**
 * Current rates. The history rows replay the past, but the end of the data cannot tell a raise
 * late in the last month from a one-off special rate. So the rates of time tracked from now on are
 * proposed per person, effective from `date` = the day after the latest imported entry (they never
 * change the replay), reviewed by the admin (flagged people first) and editable before applying:
 *  - default: the rate with the most qualifying hours (billable for bill rates, above 0, not on
 *    project-wide projects) in the person's last 30 days of history (else 90, else what the
 *    history rows give); ties: the rate used most recently;
 *  - per project used in the last 90 days: its last rate (0 for fixed-fee work); listed in
 *    `proposedProjects` when it differs from the default, otherwise the project follows it.
 * Rows are written where the history rows do not already give the confirmed rates, and for every
 * rate the admin decided (so that the next review shows it). A project that follows the default
 * gets a per-person row without rates where the history keeps it at a rate of its own: from D it
 * follows the person's rates, later changes included.
 */
export const CURRENT_FLAG_CODES = [
  /** A rate that appears only in the person's last 14 days (a raise or a special client?). */
  'late_rate',
  /**
   * The default's hours in its window come from one project, while other projects were used, and
   * nothing else was billed that rate since the project started at it (it may be a client rate).
   */
  'default_from_single_project',
  /**
   * Several rates in the window the default comes from (last 30 days, or 90) and the default is
   * not clearly the newest; or time without a project (always billed at the default) was last
   * billed another rate.
   */
  'multiple_recent_rates',
  /** A recent project that followed the default is at another rate: it may have missed a raise. */
  'project_at_old_default',
  /** The history rows give another rate on the current date (default or a recent project). */
  'history_disagrees',
  /** The last imported entry is more than 60 days before the current date, or no qualifying time in 90 days. */
  'stale_person',
  /** No default rate (no billable time at a rate above 0). */
  'no_default',
  /**
   * The person's own work was billed 0 (time without a project last billed 0, or more time at 0
   * than at the default outside project-wide projects), while the default is above 0: they may
   * have no rate of their own, and the default come from client rates.
   */
  'default_disagrees_with_own_work',
  /**
   * Other people billed a recent project at another rate after this person last used it, where
   * they had billed it the same rate before: a project-wide change this person has not billed yet.
   */
  'project_rate_changed_by_others',
  /**
   * A recent project was last used before the person's latest change of default and is proposed
   * at a rate other than the new default: it may have missed the change.
   */
  'project_last_used_before_default_change',
] as const;
export type CurrentFlagCode = (typeof CURRENT_FLAG_CODES)[number];

export type CurrentFlag = {
  code: CurrentFlagCode;
  /** The rate the flag is about; null for the person as a whole. */
  field: 'bill' | 'cost' | null;
  text: string;
};

export type CurrentFieldProposal = {
  /** Rates billed in the person's last 30 days (0 included), most hours first. null = no project. */
  recentRates: Array<{ rate: number; hours: number; projectIds: Array<string | null> }>;
  proposedDefault: number | null;
  /** Projects used in the person's last 90 days, with their last rate (by project id). */
  recentProjects: Array<{ projectId: string; lastRate: number; lastDate: string }>;
  /** Per-project current rates that differ from the default (by project id). */
  proposedProjects: Array<{ projectId: string; rate: number }>;
};

export type CurrentRatePerson = {
  userId: string;
  /** The person's last imported day. */
  lastDate: string;
  /** Current rates for this person are already stored at the current date: they are shown. */
  confirmedEarlier: boolean;
  bill: CurrentFieldProposal;
  /** null when the person's imported entries carry no cost rates. */
  cost: CurrentFieldProposal | null;
  flags: CurrentFlag[];
};

export type CurrentRates = {
  /** The day after the latest imported entry (workspace timezone); null without imported entries. */
  date: string | null;
  /** Everyone with imported entries, by user id. */
  people: CurrentRatePerson[];
};

/** Entries tracked in Taim (not imported) whose rate snapshot applying would change. */
export type RerateCount = { entries: number; running: number };

export type RateRebuildProposal = {
  /** Rows to create (not in the workspace's rates yet). */
  rows: ProposedRate[];
  /** Proposed rows that collide with an existing rate holding other values. */
  conflicts: RebuildConflict[];
  summary: {
    /** People and projects the new rows are for. */
    users: number;
    projects: number;
    rowsByScope: Record<RebuildRateScope, number>;
    /** Proposed rows already present with the same values (nothing to do). */
    unchanged: number;
  };
  /** Every imported entry resolved against the proposed rates alone. */
  replay: RebuildReplay;
  /**
   * The same check against what the workspace would have after applying without overwrite
   * (existing rates kept); null when the workspace has no rates.
   */
  replayWithExisting: RebuildReplay | null;
  /** Tomorrow in the workspace timezone: the first day the rates are checked for. */
  tomorrow: string;
  /**
   * People and person/project pairs whose rate from tomorrow, under the proposed rates alone,
   * differs from what the history suggests: person defaults first, then pairs, recent before stale.
   */
  futureChanges: FutureRateChange[];
  /** The same with existing rates kept (applying without overwrite); null when there are none. */
  futureChangesWithExisting: FutureRateChange[] | null;
  /** The same after applying with overwrite; null when there are no conflicts. */
  futureChangesWithOverwrite: FutureRateChange[] | null;
  /** Proposed runs that equal the default (see PinnedRate). */
  pinnedRates: PinnedRate[];
  /**
   * What overwrite does: existing rates at the same date that get the proposed values, proposed
   * dates added, and other existing rates of the conflicting scopes, people and projects that stay
   * (and may still apply after later dates).
   */
  overwrite: { replaces: number; adds: number; keeps: number };
  /**
   * Active people who would have no default (person) bill rate: no imported billable time at a
   * rate above 0 (`hasImportedTime` says whether they have imported time at all) and no person
   * rate set already.
   */
  peopleWithoutDefault: Array<{ userId: string; hasImportedTime: boolean }>;
  /** The workspace bill rate in effect tomorrow (what people without a rate get), if any. */
  workspaceBillRateCents: number | null;
  /** Entries tracked in Taim applying would re-rate: keeping or replacing conflicting rates. */
  rerate: { keep: RerateCount; overwrite: RerateCount };
  /** The current rates to review (see CurrentRates); `rows` holds the rows they need. */
  current: CurrentRates;
  /**
   * Identifies this proposal (rows, conflicts, existing rates, tomorrow, the re-rate counts and the
   * current rates); required to apply it.
   */
  proposalHash: string;
  /** Names for the ids above. */
  users: Array<{ id: string; name: string; email: string }>;
  projects: Array<{ id: string; name: string; clientName: string | null }>;
};

const centsSchema = z.number().int().min(0).max(100_000_000);

/**
 * The admin's changes to one person's proposed current rates (see CurrentRates). A field left out
 * keeps the proposal. `projects`: omitted or null keeps the proposed per-project rates; a list
 * replaces them (every other recent project follows the default). A listed project without a
 * value keeps its proposed rate, or follows the default.
 */
export const currentOverrideSchema = z.object({
  userId: z.uuid(),
  default: z.object({ bill: centsSchema.optional(), cost: centsSchema.optional() }).optional(),
  projects: z
    .array(z.object({ projectId: z.uuid(), bill: centsSchema.optional(), cost: centsSchema.optional() }))
    .max(1000)
    .refine((ps) => new Set(ps.map((p) => p.projectId)).size === ps.length, {
      message: 'Each project at most once',
    })
    .nullable()
    .optional(),
});
export type CurrentOverride = z.infer<typeof currentOverrideSchema>;

export const rateRebuildApplyInputSchema = z.object({
  /** The reviewed proposal's `proposalHash`: applying answers 409 when the proposal changed. */
  proposalHash: z.string().min(1).max(128),
  /**
   * Apply conflicting proposed rates too: an existing rate at the same date gets the proposed
   * values, other proposed dates are added. Existing rates at other dates are kept.
   */
  overwrite: z.boolean().default(false),
  /** Re-snapshot rates of entries tracked in Taim, running timers included (never imported ones). */
  rerateTrackedEntries: z.boolean().default(true),
  /** Changes to the proposed current rates, only for the people the admin edited. */
  currentOverrides: z
    .array(currentOverrideSchema)
    .max(10_000)
    .refine((os) => new Set(os.map((o) => o.userId)).size === os.length, {
      message: 'Each person at most once',
    })
    .default([]),
});
export type RateRebuildApplyInput = z.input<typeof rateRebuildApplyInputSchema>;

export type RateRebuildResult = {
  /** Rates added (with overwrite, also conflicting proposed rates at a date without a rate). */
  created: number;
  /** Existing rates at the same date that got the proposed values. */
  overwritten: number;
  /** Conflicting proposed rows left out because existing rates are kept. */
  skippedConflicts: number;
  /** Entries tracked in Taim whose rate snapshot changed. */
  reratedEntries: number;
};

/**
 * What applying would do (POST /api/rates/rebuild-from-import/preview, same body as applying),
 * with the admin's changes to the current rates: nothing is written.
 */
export type RateRebuildPreview = {
  /** Rates added (with overwrite, also conflicting proposed rates at a date without a rate). */
  creates: number;
  /** Existing rates at the same date that would get the proposed values (with overwrite). */
  replaces: number;
  /** Conflicting proposed rows left out (without overwrite). */
  skippedConflicts: number;
  /** Entries tracked in Taim whose rate snapshot would change (none without re-rating). */
  rerate: RerateCount;
};

/** Formats an amount in minor units as e.g. "USD 50.00" (always two decimals). */
export function formatMoney(cents: number, currency: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace(/ /g, ' ');
  } catch {
    const abs = Math.abs(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${amount < 0 ? '-' : ''}${currency} ${abs}`;
  }
}
